import { useRef, useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { Formik } from 'formik'
import moment from 'moment-timezone'
import { toZonedTime } from 'date-fns-tz'
import { useTranslation } from 'react-i18next'

import API from '../../../api/api'
import Filter from '../../Filter/Filter'
import Icon from '../../Icons'
import { selectUserRole } from '../../../reducers/userDataReducer'
import { convertToUTCTimestamp } from '../../../helpers/helpers'
import { selectBuildingsForFilter } from '../../../reducers/buildingSelectorReducer'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'
import { toastFactory } from '@/factories'

import {
  concatTagsWithBuildings,
  getMessageTypeFilterOptions,
  splitBuildingsAndTags,
  pluck,
} from './helpers'
import SmsTemplate from './templates/SmsTemplate'
import EmailOrNotificationTemplate from './templates/EmailOrNotificationTemplate'
import {
  makeEmailValidationSchema,
  makeNotificationValidationSchema,
  makeSmsValidationSchema,
} from './newMessageFormSchemas'

const NewMessageForm = ({
  handleCancel,
  mailingListOptions,
  refreshTable,
  selectedBuilding,
  previewData,
  isDuplicateAnnouncementFormActive,
  type,
  fetchAnnouncementDetails,
}) => {
  const { t } = useTranslation(['organisation'])
  const userRole = useSelector(selectUserRole)
  const buildings = useSelector(selectBuildingsForFilter)
  const timeout = useRef(null)
  const ref = useRef(null)
  const { displaySnackbar } = useSnackbar()

  const messageTypeFilterOptions = useMemo(() => {
    const options = getMessageTypeFilterOptions(selectedBuilding.communicateSMSAvailable, t).filter(
      (option) => {
        return option.value === 'sms' && !selectedBuilding?.announcementPhone ? false : true
      },
    )

    return options
  }, [selectedBuilding, t])

  const [messageType, setMessageType] = useState(
    previewData
      ? messageTypeFilterOptions.find((x) => x.value === previewData.type.toLowerCase())
      : (typeof type === 'string'
          ? messageTypeFilterOptions.find((x) => x.value === type.toLowerCase())
          : type) || messageTypeFilterOptions[0],
  )
  const [selectedMailingList, setSelectedMailingList] = useState(
    previewData
      ? previewData.mailingList
        ? mailingListOptions.find((x) => x.id === previewData.mailingList)?.value
        : mailingListOptions[0]?.value
      : mailingListOptions[0]?.value,
  )
  const [editorBody, setEditorBody] = useState({ content: previewData?.message || '', text: '' })

  const listOfBuildingsAndTags = useMemo(
    () => (userRole === 'PropertyManager' ? buildings : concatTagsWithBuildings(buildings)),
    [buildings],
  )

  const displayErrorSnackbar = (errorMessage) => {
    displaySnackbar(
      errorMessage || t('organisation:communicationPortal.newMessageForm.genericError'),
      true,
    )
  }

  const displayDuplicateSuccessToast = () => {
    if (isDuplicateAnnouncementFormActive) {
      toastFactory.success(t('organisation:communicationPortal.newMessageForm.duplicateSuccess'))
    }
  }

  const getMailingListOptionsIds = () =>
    mailingListOptions.find((x) => x.value === selectedMailingList)?.id

  const prepareTimestamp = (values) => {
    const timeToUpdate = new Date(values.deliveryTime)
    const dateToUpdate = new Date(values.deliveryDate)
    const hours = moment(timeToUpdate).format('HH')
    const minutes = moment(timeToUpdate).format('mm')
    const targetUnixTime = new Date(dateToUpdate).getTime()
    return convertToUTCTimestamp(targetUnixTime, hours, minutes)
  }

  const updateEmailCommunicationMessage = (sendData, setSubmitting) => {
    const sendDataToEdit = {
      ...sendData,
      body: editorBody.content || previewData.message,
    }

    // Note: FE does not allow to modify exisiting attachments so in order to preserve the original attachments, we delete them from the sendDataToEdit object
    delete sendDataToEdit.attachments

    API.updateScheduledEmail(sendDataToEdit, previewData.id)
      .then(() => {
        setSubmitting(false)
        handleCancel()
        toastFactory.success(t('organisation:communicationPortal.newMessageForm.editSuccess'))

        fetchAnnouncementDetails && fetchAnnouncementDetails()
        refreshTable && refreshTable()
      })
      .catch((error) => {
        setSubmitting(false)
        displayErrorSnackbar(error.response?.data?.message)
      })
  }

  const submitEmailCommunicationMessage = (
    values,
    buildingTags,
    setSubmitting,
    mailingList,
    scheduledDateTimestamp,
    buildings,
  ) => {
    const preparedUserGroups = values.userGroups.length
      ? values.userGroups.map((item) => item.value)
      : []
    const sendData = {
      ...values,
      mailingList,
      body: editorBody.content,
      header: '?',
      footer: '?',
      buildings: buildings,
      buildingTags: buildingTags,
      clientIds: pluck(values.clients),
      userGroups: pluck(preparedUserGroups, 'value'),
      unitTypes: pluck(values.unitTypes),
      clientTags: pluck(values.clientTags, 'text'),
      communityGroups: pluck(values.communityGroups),
      scheduledDate: scheduledDateTimestamp,
      buildingNumber: values.buildingNumber ? values.buildingNumber.id : '',
      floor: values.floor.length ? pluck(values.floor, 'id') : '',
      yardiPropertyId: values.yardiPropertyId ? values.yardiPropertyId.id : '',
    }
    delete sendData.clients

    if (previewData && !isDuplicateAnnouncementFormActive) {
      updateEmailCommunicationMessage(sendData, setSubmitting)
    } else {
      if (scheduledDateTimestamp) {
        API.sendScheduledEmail(sendData)
          .then(() => {
            setSubmitting(false)
            handleCancel()
            displayDuplicateSuccessToast()
            refreshTable && refreshTable()
          })
          .catch((error) => {
            setSubmitting(false)
            displayErrorSnackbar(error.response?.data?.message)
          })
      } else {
        API.sendEmail(sendData)
          .then(() => {
            setSubmitting(false)
            handleCancel()
            displayDuplicateSuccessToast()
            refreshTable && refreshTable()
          })
          .catch((error) => {
            setSubmitting(false)
            displayErrorSnackbar(error.response?.data?.message)
          })
      }
    }
  }

  const updateNotificationCommunicationMessage = (sendData, setSubmitting) => {
    const sendDataToEdit = {
      ...sendData,
      message: editorBody.text || previewData.message,
    }
    API.updateScheduledNotification(sendDataToEdit, previewData.id)
      .then(() => {
        setSubmitting(false)
        handleCancel()
        toastFactory.success(t('organisation:communicationPortal.newMessageForm.editSuccess'))

        fetchAnnouncementDetails && fetchAnnouncementDetails()
        refreshTable && refreshTable()
      })
      .catch((error) => {
        setSubmitting(false)
        displayErrorSnackbar(error.response?.data?.message)
      })
  }

  const submitNotificationCommunicationMessage = (
    values,
    buildingTags,
    setSubmitting,
    mailingList,
    scheduledDateTimestamp,
    buildings,
  ) => {
    const preparedUserGroups = values.userGroups.length
      ? values.userGroups.map((item) => item.value)
      : []

    const sendData = {
      mailingList,
      message: editorBody.text,
      title: values.title,
      image: values.attachments[0] || {},
      buildings: buildings,
      buildingTags: buildingTags,
      clientIds: pluck(values.clients),
      userGroups: pluck(preparedUserGroups, 'value'),
      unitTypes: pluck(values.unitTypes),
      clientTags: pluck(values.clientTags, 'text'),
      communityGroups: pluck(values.communityGroups),
      scheduledDate: scheduledDateTimestamp,
      buildingNumber: values.buildingNumber ? values.buildingNumber.id : '',
      floor: values.floor.length ? pluck(values.floor, 'id') : '',
      yardiPropertyId: values.yardiPropertyId ? values.yardiPropertyId.id : '',
    }

    if (previewData && !isDuplicateAnnouncementFormActive) {
      updateNotificationCommunicationMessage(sendData, setSubmitting)
    } else {
      if (scheduledDateTimestamp) {
        API.sendScheduledNotification(sendData, selectedBuilding.id)
          .then(() => {
            setSubmitting(false)
            handleCancel()
            displayDuplicateSuccessToast()
            refreshTable && refreshTable()
          })
          .catch((error) => {
            setSubmitting(false)
            displayErrorSnackbar(error.response?.data?.message)
          })
      } else {
        API.sendNotification(sendData, selectedBuilding.id)
          .then(() => {
            setSubmitting(false)
            handleCancel()
            displayDuplicateSuccessToast()
            refreshTable && refreshTable()
          })
          .catch((error) => {
            setSubmitting(false)
            displayErrorSnackbar(error.response?.data?.message)
          })
      }
    }
  }

  const submitSMSCommunicationMessage = async (setSubmitting, values, buildingTags, buildings) => {
    setSubmitting(true)

    const userGroups = values.sendTo === 'all' ? ['current', 'past'] : [values.sendTo]
    const sendData = {
      userGroups: userGroups,
      message: values.message,
      buildings,
      buildingTags,
      buildingNumber: values.buildingNumber ? values.buildingNumber.id : '',
      floor: values.floor.length ? pluck(values.floor, 'id') : '',
      yardiPropertyId: values.yardiPropertyId ? values.yardiPropertyId.id : '',
    }

    try {
      await API.sendSms(sendData)
      setSubmitting(false)
      handleCancel()
      refreshTable && refreshTable()
    } catch (error) {
      setSubmitting(false)
      displayErrorSnackbar(error.message)
    }
  }

  const handleFormSubmit = async (values, { setSubmitting }) => {
    const { buildings, buildingTags } = splitBuildingsAndTags(values.buildings)

    if (isSMS) {
      submitSMSCommunicationMessage(setSubmitting, values, buildingTags, buildings)
    } else {
      const mailingList = getMailingListOptionsIds()

      const scheduledDateTimestamp =
        values.deliveryDate && values.deliveryTime && prepareTimestamp(values)

      const _values = { ...values }
      _values.userGroups = values.clients.length
        ? values.clients.filter((client) => client.isGroup)
        : []
      // Note: Group options carry group ids, which would otherwise be sent as client ids
      _values.clients = values.clients.filter((client) => !client.isGroup)
      if (messageType.value === 'email') {
        submitEmailCommunicationMessage(
          _values,
          buildingTags,
          setSubmitting,
          mailingList,
          scheduledDateTimestamp,
          buildings,
        )
      } else if (messageType.value === 'notification') {
        submitNotificationCommunicationMessage(
          _values,
          buildingTags,
          setSubmitting,
          mailingList,
          scheduledDateTimestamp,
          buildings,
        )
      }
    }
  }

  const isSMS = messageType.value === 'sms'

  const getDeliveryDate = (previewData) => {
    if (previewData && previewData.originalDate) {
      return toZonedTime(previewData.originalDate, 'UTC')
    }
    return null
  }

  const prefilledBuilding = selectedBuilding ? [selectedBuilding] : []

  const getBuildings = (previewData, buildings) => {
    if (previewData) {
      const building = buildings.find((building) => building.id === previewData.buildingId)
      return building ? [building] : []
    }

    return prefilledBuilding
  }

  const validationSchema = {
    sms: makeSmsValidationSchema(t),
    email: makeEmailValidationSchema(t),
    notification: makeNotificationValidationSchema(t),
  }[messageType.value]

  const mapSmsUserGroupsToSendTo = (previewData) => {
    const ids = previewData?.recipientTypes?.userGroupIds || []

    if (!ids.length) return ''

    const hasCurrent = ids.includes(2)
    const hasPast = ids.includes(3)

    if (hasCurrent && hasPast) return 'all'
    if (hasCurrent) return 'current'
    if (hasPast) return 'past'

    return ''
  }

  return (
    <div className="new-message-form">
      <Formik
        enableReinitialize={true}
        initialValues={
          isSMS
            ? {
                sendTo: previewData ? mapSmsUserGroupsToSendTo(previewData) : '',
                message: previewData ? previewData.message : '',
                buildings: prefilledBuilding,
                buildingNumber: null,
                floor: previewData?.floor
                  ? previewData.floor.map((floor) => ({ id: floor, text: floor }))
                  : [],
                yardiPropertyId: null,
              }
            : {
                from: previewData?.from ? previewData.from : selectedBuilding.text || '',
                replyTo: previewData ? (previewData.replyTo ? previewData.replyTo : '') : '',
                title: previewData ? (previewData.title ? previewData.title : '') : '',
                attachments: previewData ? previewData.attachments : [],
                buildings: getBuildings(previewData, buildings),
                deliveryDate: isDuplicateAnnouncementFormActive
                  ? null
                  : getDeliveryDate(previewData),
                deliveryTime: isDuplicateAnnouncementFormActive
                  ? null
                  : getDeliveryDate(previewData),
                body: previewData ? previewData.message : '',
                clients: [],
                communityGroups: [],
                unitTypes: previewData?.recipientTypes?.unitTypes
                  ? previewData.recipientTypes.unitTypes.map((type) => ({
                      id: type,
                      text: type,
                      value: type,
                    }))
                  : [],
                clientTags:
                  previewData && previewData.recipientTypes && previewData.recipientTypes.clientTags
                    ? previewData.recipientTypes.clientTags
                    : [],
                buildingNumber: null,
                floor: previewData?.floor
                  ? previewData.floor.map((floor) => ({ id: floor, text: floor }))
                  : [],
                yardiPropertyId: null,
              }
        }
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
      >
        {({
          handleChange,
          setFieldValue,
          values,
          errors,
          touched,
          setFieldTouched,
          setSubmitting,
          isSubmitting,
          handleSubmit,
          resetForm,
        }) => {
          const setInitialValues = () => {
            resetForm()
            setSelectedMailingList(mailingListOptions[0]?.value)
          }

          const renderProperTemplate = (type) => {
            if (['email', 'notification'].includes(type)) {
              return (
                <EmailOrNotificationTemplate
                  key={type}
                  buildings={listOfBuildingsAndTags}
                  selectedBuilding={selectedBuilding}
                  mailingListOptions={mailingListOptions}
                  templateType={type}
                  previewData={previewData}
                  isDuplicateAnnouncementFormActive={isDuplicateAnnouncementFormActive}
                  handleCancel={handleCancel}
                  refreshTable={refreshTable}
                  selectedMailingList={selectedMailingList}
                  setSelectedMailingList={setSelectedMailingList}
                  getMailingListOptionsIds={getMailingListOptionsIds}
                  editorBody={editorBody}
                  setEditorBody={setEditorBody}
                  isSubmitting={isSubmitting}
                  timeout={timeout}
                  setFieldTouched={setFieldTouched}
                  handleSubmit={handleSubmit}
                  setFieldValue={setFieldValue}
                  values={values}
                  isDisabled={isDisabled}
                  handleChange={handleChange}
                  touched={touched}
                  errors={errors}
                  setSubmitting={setSubmitting}
                  userRole={userRole}
                />
              )
            } else {
              return (
                <SmsTemplate
                  buildings={listOfBuildingsAndTags}
                  values={values}
                  isDisabled={isDisabled}
                  previewData={previewData}
                  isDuplicateAnnouncementFormActive={isDuplicateAnnouncementFormActive}
                  isSubmitting={isSubmitting}
                  userRole={userRole}
                  handleSubmit={handleSubmit}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                />
              )
            }
          }
          const recipients =
            values.clients?.length ||
            values.clientTags?.length ||
            values.communityGroups?.length ||
            values.unitTypes?.length

          const emailAndNotifRequirements =
            !recipients ||
            Object.keys(errors).length ||
            !editorBody.content ||
            !getMailingListOptionsIds()

          const smsRequirements = !values.sendTo || !values.message || isSubmitting

          const isDisabled = isSMS ? smsRequirements : emailAndNotifRequirements

          return (
            <div className={`new-message-form__container ${messageType.value}-form`} ref={ref}>
              <div className="window-header">
                <div className="left-side">
                  <div className="title">
                    {previewData?.type ||
                      messageType?.text ||
                      t(
                        'organisation:communicationPortal.newMessageForm.selectMessageTypePlaceholder',
                      )}
                  </div>
                </div>
                <div className="right-side">
                  {!previewData && (
                    <Filter
                      placeholder={
                        messageType
                          ? messageTypeFilterOptions.find((x) => x.id === messageType.id).text
                          : t(
                              'organisation:communicationPortal.newMessageForm.messageTypePlaceholder',
                            )
                      }
                      options={messageTypeFilterOptions}
                      disabled={isSubmitting}
                      value={messageType ? [messageType] : []}
                      name={'MessageType'}
                      additionalClass={'ctp-communication-portal-navbar__filter'}
                      variant={'rounded'}
                      closeAfterSelect
                      onChange={(e) => {
                        setEditorBody({ content: '', text: '' })
                        setMessageType(e.target.value)
                        setInitialValues()
                      }}
                    />
                  )}
                  <div className="navi-button" onClick={handleCancel}>
                    <Icon icon="cross" />
                  </div>
                </div>
              </div>
              {renderProperTemplate(messageType.value)}
            </div>
          )
        }}
      </Formik>
    </div>
  )
}

NewMessageForm.propTypes = {
  buildings: PropTypes.array.isRequired,
  handleCancel: PropTypes.func,
  mailingListOptions: PropTypes.array,
  refreshTable: PropTypes.func,
  selectedBuilding: PropTypes.object,
  previewData: PropTypes.object,
  isDuplicateAnnouncementFormActive: PropTypes.bool,
  userRole: PropTypes.string,
  type: PropTypes.string,
  fetchAnnouncementDetails: PropTypes.func,
}

export default NewMessageForm
